import { useEffect, useRef, useState } from 'react';
import { LiveProduct, comboDiscountId } from './utils';

/**
 * Combo (BOGO placeholder) product support for the live player.
 *
 * Mirrors the storefront PDP bundle (`shopify-bundle-product`): the shopper walks
 * one step per buy/get group, picks a product + variant in each, and the *real*
 * variants are added to the cart — never the placeholder. Types and selection
 * rules are kept in sync with `shopify-bundle-product/components/ProductSelector`.
 */

export interface ComboVariant {
  id: string;
  title: string;
  price: string;
  compareAtPrice?: string | null;
  sku?: string;
  image?: string | null;
  inventoryQuantity?: number;
  selectedOptions: Array<{ name: string; value: string }>;
}

export interface ComboProductData {
  id: string;
  title: string;
  handle: string;
  description: string;
  images: Array<{ id: string; url: string; altText: string }>;
  variants: ComboVariant[];
}

export interface ComboProductItem {
  item_type: 'product' | 'collection' | 'variant';
  item_id: string;
  quantity: number;
  product_details?: {
    product_id: string;
    product_name: string;
    product_title: string;
    variant_id?: string;
    variant_title?: string;
    variant_price?: string;
    product_data?: ComboProductData;
  } | null;
}

export interface ComboGroup {
  group_index: number;
  logic_operator: 'AND' | 'OR';
  items: ComboProductItem[];
}

export interface ComboBOGOData {
  discount_id: string;
  discount_title: string;
  bogo_type: 'self' | 'non_self';
  buy_groups: ComboGroup[];
  get_groups: ComboGroup[];
  whole_discount_value: number;
}

/** One wizard step: a group plus which side of the BOGO it came from. */
export type ComboStep = ComboGroup & { type: 'buy' | 'get'; index: number };

export interface ComboSelection {
  groupType: 'buy' | 'get';
  groupIndex: number;
  itemIndex: number;
  variantId: string;
  quantity: number;
}

export type ComboOptions = Record<string, string>;

const cache = new Map<string, ComboBOGOData>();
const inflight = new Map<string, Promise<ComboBOGOData | null>>();

export async function fetchComboData(
  apiUrl: string,
  discountId: string
): Promise<ComboBOGOData | null> {
  const key = `${apiUrl}:${discountId}`;
  const cached = cache.get(key);
  if (cached) return cached;

  let pending = inflight.get(key);
  if (!pending) {
    pending = (async () => {
      try {
        const res = await fetch(`${apiUrl}/discounts/${discountId}/combo-data`);
        if (!res.ok) return null;
        const body = await res.json();
        if (!body?.success || !body.data) return null;
        const data: ComboBOGOData = {
          discount_id: body.data.discount_id,
          discount_title: body.data.discount_title,
          bogo_type: body.data.bogo_type,
          buy_groups: body.data.buy_groups || [],
          get_groups: body.data.get_groups || [],
          whole_discount_value: Number(body.data.whole_discount_value || 0),
        };
        cache.set(key, data);
        return data;
      } catch {
        return null;
      } finally {
        inflight.delete(key);
      }
    })();
    inflight.set(key, pending);
  }
  return pending;
}

export function useComboData(
  apiUrl: string,
  discountId: string | null
): { data: ComboBOGOData | null; loading: boolean } {
  const [data, setData] = useState<ComboBOGOData | null>(() =>
    discountId ? cache.get(`${apiUrl}:${discountId}`) || null : null
  );
  const [loading, setLoading] = useState(Boolean(discountId) && !data);

  useEffect(() => {
    if (!discountId) {
      setData(null);
      setLoading(false);
      return;
    }
    const cached = cache.get(`${apiUrl}:${discountId}`);
    if (cached) {
      setData(cached);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    fetchComboData(apiUrl, discountId).then((d) => {
      if (cancelled) return;
      setData(d);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [apiUrl, discountId]);

  return { data, loading };
}

/**
 * Flatten the rule into one wizard step per group.
 * `self` BOGO (buy X get X) reuses the get groups for the buy side.
 */
export function flattenComboGroups(data: ComboBOGOData): ComboStep[] {
  const buyGroups = data.bogo_type === 'self' ? data.get_groups : data.buy_groups;
  return [
    ...(buyGroups || []).map((g, i) => ({ ...g, type: 'buy' as const, index: i })),
    ...(data.get_groups || []).map((g, i) => ({ ...g, type: 'get' as const, index: i })),
  ];
}

export function stripVariantGid(variantId: string): string {
  return variantId.startsWith('gid://')
    ? variantId.replace('gid://shopify/ProductVariant/', '')
    : variantId;
}

/** Derive the option axes from the variants' selectedOptions (Shopify `options` is not returned). */
export function getProductOptions(
  variants: ComboVariant[]
): Array<{ name: string; values: string[] }> {
  if (!variants || variants.length === 0) return [];
  const optionMap = new Map<string, Set<string>>();
  variants.forEach((variant) => {
    (variant.selectedOptions || []).forEach((opt) => {
      if (!optionMap.has(opt.name)) optionMap.set(opt.name, new Set());
      optionMap.get(opt.name)!.add(opt.value);
    });
  });
  return Array.from(optionMap.entries()).map(([name, values]) => ({
    name,
    values: Array.from(values),
  }));
}

/** Exact match — every option axis must be chosen. */
export function findVariantByOptions(
  variants: ComboVariant[],
  options: ComboOptions
): ComboVariant | undefined {
  return variants.find((variant) =>
    (variant.selectedOptions || []).every((opt) => options[opt.name] === opt.value)
  );
}

/** Inventory not tracked (undefined/null) counts as available. */
export function isVariantAvailable(variant: ComboVariant | null | undefined): boolean {
  if (!variant) return true;
  if (variant.inventoryQuantity === undefined || variant.inventoryQuantity === null) {
    return true;
  }
  return variant.inventoryQuantity > 0;
}

/** Any in-stock variant matching a partial selection — drives option availability. */
export function hasAnyInStockVariantFor(
  variants: ComboVariant[],
  partialOptions: ComboOptions
): boolean {
  return variants
    .filter((variant) =>
      (variant.selectedOptions || []).every((opt) => {
        const selected = partialOptions[opt.name];
        if (selected === undefined) return true;
        return selected === opt.value;
      })
    )
    .some((v) => isVariantAvailable(v));
}

export function comboProductData(item: ComboProductItem | undefined): ComboProductData | null {
  return item?.product_details?.product_data || null;
}

function asImageUrl(value: unknown): string | null {
  if (typeof value === 'string') {
    const url = value.trim();
    if (url.startsWith('http') || url.startsWith('//')) return url;
    return null;
  }
  if (value && typeof value === 'object' && 'url' in value) {
    return asImageUrl((value as { url?: unknown }).url);
  }
  return null;
}

/** First real product photo in the combo. Placeholder products often have no storefront image. */
export function comboPreviewImage(data: ComboBOGOData | null | undefined): string | null {
  if (!data) return null;
  for (const step of flattenComboGroups(data)) {
    for (const item of step.items || []) {
      const product = comboProductData(item);
      if (!product) continue;
      const fromProduct = (product.images || []).map((img) => asImageUrl(img)).find(Boolean);
      if (fromProduct) return fromProduct;
      const fromVariant = (product.variants || []).map((v) => asImageUrl(v.image)).find(Boolean);
      if (fromVariant) return fromVariant;
    }
  }
  return null;
}

/** item_id → preview image for combo placeholders missing a catalog photo. */
export function useComboPreviewImages(
  apiUrl: string,
  products: LiveProduct[] | undefined
): Map<string, string> {
  const comboKey = (products || [])
    .filter((p) => comboDiscountId(p))
    .map((p) => `${p.item_id}:${comboDiscountId(p)}`)
    .join('|');
  const [map, setMap] = useState<Map<string, string>>(() => new Map());
  const productsRef = useRef(products);
  productsRef.current = products;

  useEffect(() => {
    const combos = (productsRef.current || []).filter((p) => comboDiscountId(p));
    if (!combos.length) {
      setMap(new Map());
      return;
    }
    let cancelled = false;
    (async () => {
      const next = new Map<string, string>();
      await Promise.all(
        combos.map(async (product) => {
          const discountId = comboDiscountId(product);
          if (!discountId) return;
          const data = await fetchComboData(apiUrl, discountId);
          const image = comboPreviewImage(data);
          if (image) next.set(String(product.item_id), image);
        })
      );
      if (!cancelled) setMap(next);
    })();
    return () => {
      cancelled = true;
    };
  }, [apiUrl, comboKey]);

  return map;
}

/** Cart line used to quote combo checkout (real variants, never the placeholder). */
export interface ComboQuoteLine {
  product_id: string;
  variant_id: string;
  price: number;
  quantity: number;
  title: string;
}

function quoteLineFromItem(
  item: ComboProductItem,
  variant?: ComboVariant | null
): ComboQuoteLine | null {
  const product = comboProductData(item);
  if (!product) return null;
  const variants = product.variants || [];
  const chosen = variant || variants.find((v) => isVariantAvailable(v)) || variants[0];
  if (!chosen) return null;
  const price = Number(chosen.price);
  return {
    product_id: product.id,
    variant_id: stripVariantGid(chosen.id),
    price: Number.isFinite(price) ? price : 0,
    quantity: item.quantity || 1,
    title: product.title,
  };
}

/** First in-stock (or first) variant from each wizard step — bag / pin quotes. */
export function defaultComboQuoteLines(data: ComboBOGOData | null | undefined): ComboQuoteLine[] {
  if (!data) return [];
  const lines: ComboQuoteLine[] = [];
  for (const step of flattenComboGroups(data)) {
    let picked: ComboQuoteLine | null = null;
    for (const item of step.items || []) {
      picked = quoteLineFromItem(item);
      if (picked) break;
    }
    if (picked) lines.push(picked);
  }
  return lines;
}

/** Map shopper-selected combo variants onto quote lines (PDP after the wizard completes). */
export function resolveComboQuoteLines(
  data: ComboBOGOData | null | undefined,
  selected: Array<{ variantId: string; quantity: number }> | null | undefined
): ComboQuoteLine[] {
  if (!data) return [];
  if (!selected?.length) return defaultComboQuoteLines(data);

  const items = flattenComboGroups(data).flatMap((step) => step.items || []);
  const resolved: ComboQuoteLine[] = [];
  for (const line of selected) {
    const vid = stripVariantGid(line.variantId);
    let found: ComboQuoteLine | null = null;
    for (const item of items) {
      const product = comboProductData(item);
      const variant = (product?.variants || []).find((v) => stripVariantGid(v.id) === vid);
      if (!product || !variant) continue;
      found = quoteLineFromItem(item, variant);
      if (found) {
        found = { ...found, quantity: line.quantity || found.quantity };
        break;
      }
    }
    if (found) resolved.push(found);
  }
  return resolved.length === selected.length ? resolved : defaultComboQuoteLines(data);
}
